import { useCallback, useEffect, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { McpUsagePanel } from '../components/features/mcp-usage/index.js';
import { ProtectedRoute } from '../components/auth/index.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useAuth } from '../hooks/index.js';
import { useMemberList } from '../hooks/useMember.js';
import {
  useMcpUsageByServer,
  useMcpUsageByTool,
  useMcpUsageByUser,
  useMcpUsageSummary,
  useMcpUsageTeams,
  useMcpUsageTimeseries,
} from '../hooks/useMcpUsage.js';
import {
  parseMcpUsageSearch,
  resolveMcpUsageSearch,
  stripMcpUsageSearchDefaults,
  type McpUsageSearch,
} from './-mcp-usage-search.js';
import type { McpUsageRange, McpUsageScope } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全社 MCP サーバー利用量ダッシュボードページ。全体 / チーム / 個人 のスコープと期間 / 集計粒度で切り替えて表示する。state は URL search params に sparse 同期し、リロード後も復元される
 * @graph-connects useAuth [calls] ログインユーザー取得
 * @graph-connects useMemberList [calls] メンバー選択肢取得
 * @graph-connects useMcpUsageTeams [calls] チームスコープの選択肢取得
 * @graph-connects useMcpUsageSummary [calls] dashboard サマリ取得
 * @graph-connects useMcpUsageTimeseries [calls] dashboard 時系列取得
 * @graph-connects useMcpUsageByServer [calls] dashboard server 別 breakdown 取得
 * @graph-connects useMcpUsageByTool [calls] dashboard tool 別 breakdown 取得
 * @graph-connects useMcpUsageByUser [calls] dashboard user 別 breakdown 取得
 * @graph-connects McpUsagePanel [calls] dashboard 描画
 */
export const Route = createFileRoute('/mcp-usage')({
  component: McpUsageRoute,
  validateSearch: parseMcpUsageSearch,
});

/**
 * ルートガード。内部メンバーのみアクセス可能（サイドバー表示と同一の SSOT で判定）。
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function McpUsageRoute() {
  return (
    <ProtectedRoute pageKey="mcpUsage">
      <McpUsagePage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function McpUsagePage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const search = Route.useSearch();
  const resolved = useMemo(() => resolveMcpUsageSearch(search), [search]);
  const navigate = useNavigate({ from: Route.fullPath });

  const updateSearch = useCallback(
    (patch: Partial<McpUsageSearch>) => {
      void navigate({
        search: (prev) => stripMcpUsageSearchDefaults({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data: teamsData } = useMcpUsageTeams({ range: resolved.range });
  const { data: memberListData } = useMemberList();

  const teamOptions = useMemo(() => {
    return (teamsData?.data ?? [])
      .map((team) => ({ value: team.teamName, label: team.teamName }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [teamsData]);

  const memberOptions = useMemo(() => {
    if (!memberListData) return [];
    const all = [...memberListData.engineers, ...memberListData.qa];
    return all
      .map((m) => ({ value: m.email, label: `${m.name} (${m.email})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [memberListData]);

  const periodOptions = [
    { value: '7d', label: t('time.last7Days') },
    { value: '14d', label: t('time.last14Days') },
    { value: '30d', label: t('time.last30Days') },
    { value: '90d', label: t('time.last90Days') },
    { value: '180d', label: t('time.last180Days') },
    { value: '365d', label: t('time.last1Year') },
  ];

  const scopeOptions = [
    { value: 'overall', label: t('mcpUsage.scope.overall') },
    { value: 'team', label: t('mcpUsage.scope.team') },
    { value: 'member', label: t('mcpUsage.scope.member') },
  ];

  // スコープ切替時に初期選択を補う (URL に既に乗っていれば触らない)
  useEffect(() => {
    if (resolved.scope === 'team' && !resolved.team && teamOptions[0]) {
      updateSearch({ team: teamOptions[0].value });
    }
  }, [resolved.scope, resolved.team, teamOptions, updateSearch]);

  useEffect(() => {
    if (resolved.scope === 'member' && !resolved.email && user?.email) {
      updateSearch({ email: user.email });
    }
  }, [resolved.scope, resolved.email, user?.email, updateSearch]);

  const queryParams = useMemo(() => {
    if (resolved.scope === 'team') {
      return { scope: resolved.scope, range: resolved.range, team: resolved.team };
    }
    if (resolved.scope === 'member') {
      return { scope: resolved.scope, range: resolved.range, email: resolved.email };
    }
    return { scope: 'overall' as const, range: resolved.range };
  }, [resolved.scope, resolved.range, resolved.team, resolved.email]);

  const summaryQuery = useMcpUsageSummary(queryParams);
  const timeseriesQuery = useMcpUsageTimeseries({
    ...queryParams,
    granularity: resolved.granularity,
  });
  const byServerQuery = useMcpUsageByServer(queryParams);
  const byToolQuery = useMcpUsageByTool(queryParams);
  const byUserQuery = useMcpUsageByUser(queryParams);

  const scopeLabel = useMemo(() => {
    if (resolved.scope === 'team') return resolved.team ?? t('mcpUsage.scope.team');
    if (resolved.scope === 'member') return resolved.email ?? t('mcpUsage.scope.member');
    return t('mcpUsage.scope.overall');
  }, [resolved.scope, resolved.team, resolved.email, t]);

  const emptyMessage =
    resolved.scope === 'team' && !resolved.team
      ? t('mcpUsage.emptyStates.selectTeam')
      : resolved.scope === 'member' && !resolved.email
        ? t('mcpUsage.emptyStates.selectMember')
        : null;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          {t('mcpUsage.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t('mcpUsage.description')}</p>
      </header>
      <Card padding="none">
        <CardContent className="flex flex-wrap items-end gap-3 !mt-0 p-3">
          <div className="min-w-[160px] flex-1">
            <Select
              label={t('mcpUsage.scopeSelectLabel')}
              value={resolved.scope}
              options={scopeOptions}
              onChange={(event) => {
                updateSearch({ scope: event.target.value as McpUsageScope });
              }}
              fullWidth
            />
          </div>
          {resolved.scope === 'team' ? (
            <div className="min-w-[200px] flex-1">
              <Select
                label={t('mcpUsage.teamSelectLabel')}
                value={resolved.team ?? ''}
                options={teamOptions}
                onChange={(event) => {
                  updateSearch({ team: event.target.value || undefined });
                }}
                placeholder={t('mcpUsage.placeholder.team')}
                fullWidth
              />
            </div>
          ) : null}
          {resolved.scope === 'member' ? (
            <div className="min-w-[240px] flex-1">
              <Select
                label={t('mcpUsage.memberSelectLabel')}
                value={resolved.email ?? ''}
                options={memberOptions}
                onChange={(event) => {
                  updateSearch({ email: event.target.value || undefined });
                }}
                placeholder={t('mcpUsage.placeholder.member')}
                fullWidth
              />
            </div>
          ) : null}
          <div className="min-w-[160px] flex-1">
            <Select
              label={t('mcpUsage.periodSelectLabel')}
              value={resolved.range}
              options={periodOptions}
              onChange={(event) => {
                updateSearch({ range: event.target.value as McpUsageRange });
              }}
              fullWidth
            />
          </div>
        </CardContent>
      </Card>
      <McpUsagePanel
        scopeLabel={scopeLabel}
        summary={summaryQuery.data}
        isSummaryLoading={summaryQuery.isLoading}
        timeseries={timeseriesQuery.data}
        byServer={byServerQuery.data}
        byTool={byToolQuery.data}
        byUser={byUserQuery.data}
        emptyMessage={emptyMessage}
        granularity={resolved.granularity}
        onGranularityChange={(value) => {
          updateSearch({ granularity: value });
        }}
      />
    </div>
  );
}
